import { ossBucketFlags } from '@/settings'

export const OSS_IMAGE_WEBP_PROCESS = 'image/format,webp'

const SIGNATURE_PARAM_PATTERN = /(?:^|[?&])(?:OSSAccessKeyId|Signature|Expires|x-oss-signature|x-oss-credential|x-oss-date)=/i
const BITMAP_EXTENSION_PATTERN = /\.(?:avif|bmp|jpe?g|png|webp)$/i
const EXCLUDED_DISPLAY_PATH_PATTERN = /(?:^|[/_.-])(?:logo|qr(?:code)?|captcha|verification[_-]?code|pay(?:ment)?[_-]?code)(?:[/_.-]|$)/i

function includesBucketFlag(url, flags) {
  const normalizedUrl = url.toLowerCase()
  return (Array.isArray(flags) ? flags : []).some(flag => normalizedUrl.includes(String(flag).toLowerCase()))
}

function decodeQueryComponent(value) {
  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

function isPublicOssBitmapUrl(url) {
  if (!/^https?:\/\//i.test(url) || SIGNATURE_PARAM_PATTERN.test(url)) return false

  let parsedUrl
  try {
    parsedUrl = new URL(url)
  } catch (error) {
    return false
  }

  if (!BITMAP_EXTENSION_PATTERN.test(parsedUrl.pathname)) return false
  if (EXCLUDED_DISPLAY_PATH_PATTERN.test(parsedUrl.pathname)) return false
  if (includesBucketFlag(url, ossBucketFlags.private)) return false

  const host = parsedUrl.hostname.toLowerCase()
  return includesBucketFlag(url, ossBucketFlags.def) ||
    host.startsWith('osspub.') ||
    host.startsWith('puboss.')
}

export function appendWebpToOssProcess(process, requestedProcess = '') {
  const currentProcess = String(process || '').trim().replace(/^\/+|\/+$/g, '')
  const fallbackProcess = String(requestedProcess || '').trim().replace(/^\/+|\/+$/g, '')
  let processPrefix = (currentProcess || fallbackProcess)
    .replace(/(?:^|\/)format,[^/]+/ig, '')
    .replace(/^\/+|\/+$/g, '') || 'image'
  const requestedResize = fallbackProcess.match(/(?:^|\/)(resize,[^/]+)/i)?.[1] || ''
  if (requestedResize && !/(?:^|\/)resize,[^/]+/i.test(processPrefix)) {
    processPrefix = `${processPrefix}/${requestedResize}`
  }
  return `${processPrefix}/format,webp`
}

function getProcessedImageDisplayUrl(value, requestedProcess) {
  if (typeof value !== 'string') return value

  const sourceUrl = value.trim()
  if (!sourceUrl || !isPublicOssBitmapUrl(sourceUrl)) return value

  const hashIndex = sourceUrl.indexOf('#')
  const hash = hashIndex >= 0 ? sourceUrl.slice(hashIndex) : ''
  const urlWithoutHash = hashIndex >= 0 ? sourceUrl.slice(0, hashIndex) : sourceUrl
  const queryIndex = urlWithoutHash.indexOf('?')
  const baseUrl = queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash
  const queryParts = queryIndex >= 0
    ? urlWithoutHash.slice(queryIndex + 1).split('&').filter(Boolean)
    : []
  let processInsertIndex = -1
  let currentProcess = ''
  const normalizedQueryParts = []

  queryParts.forEach(part => {
    const equalsIndex = part.indexOf('=')
    const rawKey = equalsIndex >= 0 ? part.slice(0, equalsIndex) : part
    if (decodeQueryComponent(rawKey).toLowerCase() !== 'x-oss-process') {
      normalizedQueryParts.push(part)
      return
    }

    if (processInsertIndex < 0) {
      processInsertIndex = normalizedQueryParts.length
      currentProcess = decodeQueryComponent(equalsIndex >= 0 ? part.slice(equalsIndex + 1) : '')
      return
    }

    const additionalProcess = decodeQueryComponent(equalsIndex >= 0 ? part.slice(equalsIndex + 1) : '')
      .trim()
      .replace(/^\/?image\/?/i, '')
      .replace(/^\/+|\/+$/g, '')
    if (additionalProcess && !/^format,/i.test(additionalProcess)) {
      currentProcess = `${currentProcess.replace(/\/+$/g, '')}/${additionalProcess}`
    }
  })

  const processPart = `x-oss-process=${appendWebpToOssProcess(currentProcess, requestedProcess)}`
  normalizedQueryParts.splice(processInsertIndex < 0 ? normalizedQueryParts.length : processInsertIndex, 0, processPart)
  return `${baseUrl}?${normalizedQueryParts.join('&')}${hash}`
}

export function getImageDisplayUrl(value) {
  return getProcessedImageDisplayUrl(value, OSS_IMAGE_WEBP_PROCESS)
}

export function getImageDisplayUrls(values) {
  return Array.isArray(values) ? values.map(getImageDisplayUrl) : values
}

export function getResizedImageDisplayUrl(value, width, height = width) {
  const safeWidth = Math.max(1, Number(width) || 1)
  const safeHeight = Math.max(1, Number(height) || safeWidth)
  return getProcessedImageDisplayUrl(
    value,
    `image/resize,w_${safeWidth},h_${safeHeight}`
  )
}

export function transformHtmlImageSources(html) {
  if (typeof html !== 'string' || !html || typeof document === 'undefined') return html

  const template = document.createElement('template')
  template.innerHTML = html
  template.content.querySelectorAll('img[src]').forEach(image => {
    const originalSrc = image.getAttribute('src')
    const displaySrc = getImageDisplayUrl(originalSrc)
    if (displaySrc === originalSrc) return

    if (!image.hasAttribute('data-original-src')) {
      image.setAttribute('data-original-src', originalSrc)
    }
    image.setAttribute('src', displaySrc)
  })
  return template.innerHTML
}
